import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../util/api";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import {
    Box, Button, Chip, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import ResizableTable from "../../../component/common/ResizableTable";
import ActionButton from "../../../component/common/ActionButton";
import TableToolbar from "../../../component/common/TableToolbar";
import { useTableSearch } from "../../../util/tableSearch";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";
import { applyRangeEndChange, applyRangeStartChange, today as getToday } from "../../../util/dateUtils";
import FormField from "../../../component/common/FormField";
import LoadingDots from "../../../component/common/LoadingDots";

const BASE = process.env.REACT_APP_API_REAL_URL;

const now = getToday();

const EMPTY = {
    companyName: "",
    contactName: "",
    startDate: now.format("YYYY-MM-DD"),
    endDate: now.add(30, "day").format("YYYY-MM-DD"),
    note: "",
};

const statusMeta = {
    ACTIVE:  { label: "활성",  color: "#16a34a", bg: "#f0fdf4" },
    REVOKED: { label: "취소됨", color: "#dc2626", bg: "#fef2f2" },
};

export default function HrExternalViewersTab() {
    const [rows, setRows] = useState([]);
    // 표 안 검색 — 공통 유틸이 행 전체를 훑는다.
    const [tableQuery, setTableQuery] = useState("");
    const rowsFiltered = useTableSearch(rows, tableQuery);

    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [dateDialog, setDateDialog] = useState(null); // {id, startDate, endDate}

    const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
    const sortLabel = (key, label) => `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    const sortedRows = useMemo(() => {
        return [...rowsFiltered].sort((a, b) => {
            const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
            const cmp = String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [rowsFiltered, sort]);

    const load = () => {
        setLoading(true);
        api.get(`${BASE}/api/hr/external-viewers`)
            .then(data => setRows(Array.isArray(data) ? data : (data?.data || [])))
            .catch(() => notify("목록 로딩 실패", "error"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleCreate = () => {
        if (!form.companyName || !form.contactName || !form.startDate || !form.endDate) {
            notify("모든 필드를 입력하세요.", "warning");
            return;
        }
        return runApiAction({
            request: () => api.post(`${BASE}/api/hr/external-viewers`, form),
            successMsg: "임시 계정이 생성되었습니다.",
            errorMsg: serverMsgOr("생성 실패"),
            onSuccess: () => { setForm(EMPTY); load(); },
            closeOnSuccess: () => setOpen(false),
        });
    };

    const handleToggleStatus = async (row) => {
        const newStatus = row.status === "ACTIVE" ? "REVOKED" : "ACTIVE";
        const label = newStatus === "REVOKED" ? "취소" : "활성화";
        if (!(await confirmDialog({ message: `${row.companyName} (${row.contactName}) 계정을 ${label}하시겠습니까?` }))) return;
        return runApiAction({
            request: () => api.patch(`${BASE}/api/hr/external-viewers/${row.id}/status`, null, { params: { status: newStatus } }),
            successMsg: `${label}되었습니다.`,
            errorMsg: serverMsgOr("처리 실패"),
            onSuccess: load,
        });
    };

    const handleDelete = async (row) => {
        if (!(await confirmDialog({ message: `${row.companyName} 계정을 삭제하시겠습니까?` }))) return;
        return runApiAction({
            request: () => api.delete(`${BASE}/api/hr/external-viewers/${row.id}`),
            successMsg: "삭제되었습니다.",
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: load,
        });
    };

    const handleUpdateDates = () => {
        if (!dateDialog) return;
        return runApiAction({
            request: () => api.patch(`${BASE}/api/hr/external-viewers/${dateDialog.row.id}/dates`,
                { startDate: dateDialog.startDate, endDate: dateDialog.endDate }),
            successMsg: "기간이 수정되었습니다.",
            onSuccess: () => { setDateDialog(null); load(); },
        });
    };

    const linkRef = useRef(null);

    // 보이는 입력칸을 직접 선택 후 복사 — HTTP(비보안 컨텍스트)에서도 확실히 동작.
    const handleCopyLink = async () => {
        const el = linkRef.current;
        if (!el) return;
        el.focus();
        el.select();
        const link = el.value;
        let ok = false;
        if (navigator.clipboard && window.isSecureContext) {
            try { await navigator.clipboard.writeText(link); ok = true; } catch { /* 폴백으로 */ }
        }
        if (!ok) { try { ok = document.execCommand("copy"); } catch { ok = false; } }
        notify(
            ok ? "링크가 복사되었습니다." : "링크가 선택되었어요 — Ctrl/⌘+C 로 복사하세요.",
            ok ? "success" : "warning"
        );
    };

    const fmtDt = (dt) => {
        if (!dt) return "—";
        return dt.replace("T", " ").substring(0, 16);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                        키비주얼 임시 접속 계정
                        <span style={{ color: ds.colors.textMuted, fontWeight: 400, fontSize: 13 }}>
                            {" "}— 외부 업체 열람 관리
                        </span>
                    </Typography>
                    {/* 검색 — 동작 버튼 왼쪽, 우측 상단 고정(전 시스템 공통 배치) */}
                    <TableToolbar onSearch={setTableQuery} placeholder="계정·이름 검색"
                        sx={{ flex: 1, mb: 0, minWidth: 200 }} />
                    <ActionButton variant="add" onClick={() => setOpen(true)} label="계정 생성" />
                </Box>
                <Divider />

                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("companyName")}>{sortLabel("companyName", "업체명")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("contactName")}>{sortLabel("contactName", "담당자")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("startDate")}>{sortLabel("startDate", "시작일")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("endDate")}>{sortLabel("endDate", "만료일")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("lastAccessAt")}>{sortLabel("lastAccessAt", "마지막 접속")}</TableCell>
                            <TableCell>마지막 IP</TableCell>
                            <TableCell>등록자</TableCell>
                            <TableCell>메모</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: ds.colors.textMuted }}>
                                    등록된 임시 계정이 없습니다.
                                </TableCell>
                            </TableRow>
                        )}
                        {sortedRows.map(row => {
                            const sm = statusMeta[row.status] || statusMeta.ACTIVE;
                            return (
                                <TableRow key={row.id} hover sx={{ cursor: "pointer" }}
                                    onClick={() => setDateDialog({ row, startDate: row.startDate, endDate: row.endDate })}>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.companyName}</TableCell>
                                    <TableCell>{row.contactName}</TableCell>
                                    <TableCell>{row.startDate}</TableCell>
                                    <TableCell sx={{ color: row.expired ? "#dc2626" : "inherit", fontWeight: row.expired ? 700 : 400 }}>
                                        {row.endDate}
                                        {row.expired && <Typography component="span" sx={{ fontSize: 10, ml: 0.5, color: "#dc2626" }}>만료</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={sm.label} size="small"
                                            sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 700 }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#475569" }}>{fmtDt(row.lastAccessAt)}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#64748b", fontFamily: "monospace" }}>{row.lastAccessIp || "—"}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{row.createdByName || "—"}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{row.note || "—"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 계정 생성 다이얼로그 */}
            <FormModal
                open={open}
                onClose={() => setOpen(false)}
                title="임시 접속 계정 생성"
                maxWidth="xs"
                onSave={handleCreate}
                saveLabel="생성"
                saveDisabled={!form.companyName || !form.contactName}
            >
                <Stack spacing={2}>
                    <FormField label="업체명" required>
                        <TextField size="small" fullWidth
                            value={form.companyName}
                            onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))}
                            placeholder="업체명을 입력하세요" />
                    </FormField>
                    <FormField label="담당자명" required>
                        <TextField size="small" fullWidth
                            value={form.contactName}
                            onChange={e => setForm(p => ({ ...p, contactName: e.target.value }))}
                            placeholder="담당자명을 입력하세요" />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="시작일" required sx={{ flex: 1 }}>
                            <TextField size="small" type="date" fullWidth
                                value={form.startDate}
                                onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startDate", endKey: "endDate", isDate: true }))} />
                        </FormField>
                        <FormField label="만료일" required sx={{ flex: 1 }}>
                            <TextField size="small" type="date" fullWidth
                                value={form.endDate}
                                onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startDate", endKey: "endDate" }))} />
                        </FormField>
                    </Box>
                    <FormField label="메모">
                        <TextField size="small" fullWidth
                            value={form.note}
                            onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                            placeholder="예: 2026 봄 페스티벌 미팅" />
                    </FormField>
                    <Typography sx={{ fontSize: 12, color: "#64748b", bgcolor: "#f8fafc", p: 1.5, borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                        계정 생성 후 접속 링크를 복사해서 업체에 전달하세요.
                        링크에 포함된 접속 코드로 기간 내 키비주얼을 열람할 수 있습니다.
                    </Typography>
                </Stack>
            </FormModal>

            {/* 기간 수정 + 계정 관리 다이얼로그 (행 클릭) */}
            <FormModal
                open={!!dateDialog}
                onClose={() => setDateDialog(null)}
                title={dateDialog ? `${dateDialog.row.companyName} — 접속 기간 수정` : "접속 기간 수정"}
                subtitle={dateDialog ? `${dateDialog.row.contactName || ""}${dateDialog.row.status === "ACTIVE" ? " · 활성" : " · 취소됨"}` : undefined}
                maxWidth="xs"
                onSave={handleUpdateDates}
                saveLabel="기간 저장"
                footerExtras={dateDialog && (
                    <Stack direction="row" spacing={0.75}>
                        <ActionButton variant="secondary" startIcon={false}
                            onClick={() => { const r = dateDialog.row; setDateDialog(null); handleToggleStatus(r); }}
                            sx={{ fontSize: 12,
                                color: dateDialog.row.status === "ACTIVE" ? "#dc2626" : "#16a34a",
                                borderColor: dateDialog.row.status === "ACTIVE" ? "rgba(220,38,38,0.35)" : "rgba(22,163,74,0.35)",
                                "&:hover": { borderColor: dateDialog.row.status === "ACTIVE" ? "#dc2626" : "#16a34a" } }}
                            label={dateDialog.row.status === "ACTIVE" ? "접속취소" : "활성화"} />
                        <ActionButton variant="delete" startIcon={false}
                            onClick={() => { const r = dateDialog.row; setDateDialog(null); handleDelete(r); }}
                            sx={{ fontSize: 12 }} />
                    </Stack>
                )}
            >
                <Stack spacing={2}>
                    <FormField label="외부 접속 링크">
                        <TextField
                            size="small" fullWidth
                            inputRef={linkRef}
                            value={dateDialog ? `${window.location.origin}/external-keyvisual?code=${dateDialog.row.accessCode}` : ""}
                            inputProps={{ onClick: (e) => e.target.select() }}
                            InputProps={{
                                readOnly: true,
                                sx: { fontSize: 12, fontFamily: "monospace" },
                                endAdornment: (
                                    <Tooltip title="복사">
                                        <IconButton size="small" edge="end" onClick={handleCopyLink}>
                                            <ContentCopyIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            }}
                            helperText="클릭하면 전체 선택됩니다 — Ctrl/⌘+C 로도 복사할 수 있어요."
                        />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="시작일" sx={{ flex: 1 }}>
                            <TextField size="small" type="date" fullWidth
                                value={dateDialog?.startDate || ""}
                                onChange={e => setDateDialog(p => applyRangeStartChange(p, e.target.value, { startKey: "startDate", endKey: "endDate", isDate: true }))} />
                        </FormField>
                        <FormField label="만료일" sx={{ flex: 1 }}>
                            <TextField size="small" type="date" fullWidth
                                value={dateDialog?.endDate || ""}
                                onChange={e => setDateDialog(p => applyRangeEndChange(p, e.target.value, { startKey: "startDate", endKey: "endDate" }))} />
                        </FormField>
                    </Box>
                </Stack>
            </FormModal>
        </Box>
    );
}
